import type { FC, PropsWithChildren } from 'react'
import React from 'react'
import styled from '@emotion/styled'
import Image from 'next/image'

import theme from '@segment/matcha/foundations/Theme'

import LogoTile from '@segment/matcha/components/LogoTile'

import * as Hero from '@segment/matcha/recipes/Hero'

import type { Integration } from '@segment/api/staticSiteBackend/integration'

import contentfulImageLoader from '@segment/utils/contentfulImageLoader'

interface LogoTitleBodyImageProps {
  integration: Integration
}

const LogoTitleBodyImage: FC<PropsWithChildren<LogoTitleBodyImageProps>> = ({ integration }) => {
  return (
    <Wrapper>
      <LogoImage>
        <StyledLogoTile size="fill" elevation={2}>
          <svg width="54" viewBox="0 0 54 57" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M51.325 24.833h-27.65a1.942 1.942 0 0 1-1.925-1.925v-3.325c0-1.05.875-1.925 1.925-1.925H51.5c1.05 0 1.925.875 1.925 1.925v3.325c-.175 1.05-1.05 1.925-2.1 1.925ZM30.325 38.833H2.675A1.942 1.942 0 0 1 .75 36.908v-3.325c0-.875.875-1.75 1.925-1.75H30.5c1.05 0 1.925.875 1.925 1.925v3.325c-.175.875-1.05 1.75-2.1 1.75ZM11.25 51.083a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM42.75 12.583a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM27 49.333c-1.925 0-3.85-.35-5.6-.875-.875-.175-1.75.175-2.1 1.05l-1.225 3.325c-.35.875.175 1.925 1.225 2.275 2.45.875 5.075 1.225 7.7 1.225 11.375 0 21.175-6.825 25.55-16.625.35-.875 0-2.1-1.05-2.45l-3.325-1.225c-.875-.35-1.75 0-2.1.875-3.325 7.35-10.5 12.425-19.075 12.425ZM27 7.333c1.925 0 3.85.35 5.6.875.875.175 1.75-.175 2.1-1.05l1.225-3.325c.35-.875-.175-1.925-1.225-2.275C32.25.683 29.625.333 27 .333 15.625.333 5.825 7.158 1.45 16.958c-.35.875 0 2.1 1.05 2.45l3.325 1.225c.875.35 1.75 0 2.1-.875C11.25 12.408 18.425 7.333 27 7.333Z"
              fill="#25C365"
            />
          </svg>
        </StyledLogoTile>
        <StyledIcon>
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="29" viewBox="0 0 28 29" fill="none">
            <path
              fill="#394762"
              fillRule="evenodd"
              d="M11.667 26a2.333 2.333 0 1 0 4.666 0v-9.333h9.334a2.333 2.333 0 0 0 0-4.667h-9.334V2.667a2.333 2.333 0 1 0-4.666 0V12H2.333a2.333 2.333 0 0 0 0 4.667h9.334V26Z"
              clipRule="evenodd"
            />
          </svg>
        </StyledIcon>

        <StyledLogoTile size="fill" variant="solid" elevation={2}>
          {integration?.logo && (
            <Image
              loader={contentfulImageLoader}
              src={integration?.logo.fields.file.url}
              alt={integration?.name}
              width={88}
              height={88}
            />
          )}
        </StyledLogoTile>
      </LogoImage>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  margin: 0 auto 40px;
  max-width: 400px;
  position: relative;
  width: 82%;

  ${theme.breakpoints.up(`sm`)} {
    margin-bottom: 80px;
  }

  &::before,
  &:after {
    content: '';
    display: block;
    width: 50.5%;
    height: 100%;
    position: absolute;
    top: 0;
    border-radius: 16px;
    border: 2px solid transparent;
    mask-composite: exclude;
  }

  &::before {
    left: 0;
    background: linear-gradient(45deg, #cacdd8 0%, rgba(202, 205, 216, 0) 25%, rgba(202, 205, 216, 0) 75%, #cacdd8 100%)
      border-box;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    z-index: 1;
  }

  &::after {
    right: 0;
    border-radius: 16px;
    border: 2px solid transparent;
    background: linear-gradient(45deg, rgba(202, 205, 216, 0) 25%, rgba(202, 205, 216, 0) 75%, #cacdd8 100%) border-box;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    transform: rotate(-180deg);
  }
`

const LogoImage = styled(Hero.Contents)`
  display: flex;
  align-items: center;
  margin: 0 auto 40px;
  position: relative;

  ${theme.breakpoints.up(`sm`)} {
    margin-bottom: 80px;
  }

  &:before,
  &:after {
    content: '';
    display: block;
    width: 50.5%;
    height: 100%;
    background: linear-gradient(135deg, #ffffff 19.39%, rgba(244, 244, 246, 0.5) 59.7%, rgba(244, 244, 246, 0.5) 100%);
    flex: 0 0 auto;
    border-radius: 16px;
    position: absolute;
    top: 0;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
    transform: rotate(-180deg);
  }
`

const StyledLogoTile = styled(LogoTile)`
  margin: 11%;
  position: relative;
  z-index: 2;
  border-radius: 16px;
  border: 2px solid ${({ theme }) => theme.palette.twilio.gray[20]};

  svg {
    height: 60%;
    width: 60%;
  }
`

const StyledIcon = styled.div`
  width: 18px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;

  ${theme.breakpoints.up(`md`)} {
    width: 28px;
  }
`

export default LogoTitleBodyImage
